<template>
  <transition name="slide-fade">
    <div v-if="showNotification" :class="['internet-notification', notificationClass]">
      <div class="notification-content">
        <i :class="notificationIcon"></i>
        <span>{{ notificationMessage }}</span>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "InternetDetectionVue",
  data() {
    return {
      isOnline: navigator.onLine,
      showNotification: false,
      notificationTimeout: null,
      checkInterval: null,
      isInitialLoad: true,
    };
  },
  computed: {
    notificationClass() {
      return this.isOnline ? "online" : "offline";
    },
    notificationIcon() {
      return this.isOnline ? "bi bi-wifi" : "bi bi-wifi-off";
    },
    notificationMessage() {
      return this.isOnline
        ? "Connexion internet rétablie"
        : "Pas de connexion internet";
    },
  },
  mounted() {
    // Listen to online/offline events
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    // Set initial status based on navigator.onLine
    this.isOnline = navigator.onLine;
    
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 1000);

    // Periodic connection check every 30 seconds (only after initial load)
    this.checkInterval = setInterval(() => {
      if (!this.isInitialLoad) {
        this.checkConnection();
      }
    }, 30000);
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },
  methods: {
    handleOnline() {
      console.log("Network status: online");
      this.updateConnectionStatus(true);
    },
    handleOffline() {
      console.log("Network status: offline");
      this.updateConnectionStatus(false);
    },
    updateConnectionStatus(isOnline) {
      const previousStatus = this.isOnline;
      this.isOnline = isOnline;

      // Only show notification if status actually changed AND it's not the initial load
      if (previousStatus !== isOnline && !this.isInitialLoad) {
        this.displayNotification();
      }
    },
    displayNotification() {
      this.showNotification = true;

      // Clear any existing timeout
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      // Auto-hide only for online notification
      if (this.isOnline) {
        this.notificationTimeout = setTimeout(() => {
          this.showNotification = false;
        }, 4000);
      }
      // Offline notification stays visible until connection is restored
    },
    async checkConnection() {
      // First check navigator.onLine (fast and reliable)
      if (!navigator.onLine) {
        this.updateConnectionStatus(false);
        return;
      }

      // If navigator says online, verify with a lightweight request
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        // Use a more reliable endpoint - try the current origin
        const response = await fetch(window.location.origin + "/favicon.ico", {
          method: "HEAD",
          cache: "no-cache",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Consider it online if we get any response (even 404 means we have connection)
        const newStatus = response.status < 500;
        this.updateConnectionStatus(newStatus);
      } catch (error) {
        // If fetch fails, check navigator.onLine as fallback
        this.updateConnectionStatus(navigator.onLine);
      }
    },
  },
};
</script>

<style scoped>
.internet-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  padding: 14px 22px;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  min-width: 280px;
  backdrop-filter: blur(10px);
}

.internet-notification.online {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.internet-notification.offline {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  font-size: 15px;
}

.notification-content i {
  font-size: 20px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Transition animations */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@media (max-width: 768px) {
  .internet-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    padding: 12px 18px;
  }

  .notification-content {
    font-size: 14px;
  }

  .notification-content i {
    font-size: 18px;
  }
}
</style>